// Agenda par ville (France entiere) : de quelle ville parle la page, et le
// selecteur qui permet d'en changer.
//
// La ville est dans l'adresse. Paris garde les adresses historiques (/, /salles,
// /artistes/…) ; une autre ville a les memes rubriques sous son nom (/lyon/,
// /lyon/salles/…), pages generees au build par scrapers/generate_city_pages.py
// qui y pose <meta name="lagenda-ville">.
//   - page de rubrique (agenda, salles…) : la ville de l'adresse ; changer de
//     ville mene a la meme rubrique dans l'autre ville ;
//   - guide (/lyon/concerts/ce-soir/, servi par reecriture vers guide-view.html,
//     donc sans <meta>) : la ville est le 1er segment de l'adresse ; changer de
//     ville mene au meme guide dans l'autre ville s'il y existe ;
//   - accueil / : la ville de la personne — choix memorise, sinon ville de la
//     connexion (IP, premiere visite seulement), sinon Paris — par redirection ;
//   - fiche artiste (/artiste/x/, nationale) : la ville choisie, sinon Paris ;
//     elle s'abonne a onChange pour se rearranger sur place ;
//   - pages producteur (/producteur/x/) et annuaire (/producteurs) : idem,
//     dates et producteurs de la ville choisie d'abord, puis la France ;
//   - toute autre page sans ville dans l'adresse (guides, genres, fiches concert
//     et salles de Paris) parle de Paris ; changer de ville mene a l'agenda de
//     la ville choisie.
//
// Donnees : /data/villes.json (scrapers/villes.py). Une ville = une commune ou
// l'on suit une salle + ses voisines (Floirac et Cenon sont a Bordeaux). Si le
// fichier manque, le module se tait et le site reste sur Paris.
//
// API :
//   LAgendaVilles.ready             Promise, resolue quand la ville est connue
//                                   (jamais quand l'accueil part vers une autre ville)
//   LAgendaVilles.current()         la ville de la page ({slug, nom, a_nom, sources…}) ou null
//   LAgendaVilles.choisie()         la ville que la personne a choisie (memorisee), ou null :
//                                   Paris par defaut n'est pas un choix (pages producteur)
//   LAgendaVilles.metaVille         slug de la <meta name="lagenda-ville"> (pages de ville), ou null
//   LAgendaVilles.sources()         Promise : fichiers de dates de la page (index, sinon adresse, sinon Paris)
//   LAgendaVilles.fetchSources(l)   Promise : dates de ces fichiers (1er exige, suivants facultatifs)
//   LAgendaVilles.events()          Promise : dates de la page (sources() + fetchSources())
//   LAgendaVilles.villes()          toutes les villes (index)
//   LAgendaVilles.salle(nom)        salle de province : {ville, commune, slug, capacite, prep, nom…}
//   LAgendaVilles.sallesDeLaVille() salles de province de la ville courante
//   LAgendaVilles.salles()          toutes les salles de province
//   LAgendaVilles.salleParSlug(slug) salle de province par son adresse /salle/<slug>/
//   LAgendaVilles.ville(slug)       une ville de l'index
//   LAgendaVilles.taille(salle)     rubrique de taille d'une salle de province (comme venues-data.js)
//   LAgendaVilles.couleur(slug)     couleur d'une salle de province, stable d'une page a l'autre
//   LAgendaVilles.url(slug, rubrique) adresse d'une rubrique dans une ville
//   LAgendaVilles.onChange(fn)      appele apres un changement de ville sur place
//   LAgendaVilles.open()            ouvre le selecteur
(function(){
'use strict';

const STORE_KEY = 'lagenda:ville';
// Geolocalisation par IP : GeoJS (gratuit, sans cle, CORS ouvert). Le service
// voit l'IP du visiteur : mentionne dans /confidentialite, autorise par la CSP
// (connect-src) de firebase.json.
const GEO_IP_URL = 'https://get.geojs.io/v1/ip/geo.json';
// L'accueil attend cette reponse avant de choisir sa ville (le fichier de Paris,
// lui, part sans attendre : events()) : au-dela, Paris.
const IP_TIMEOUT_MS = 800;
// IP sans ville (etranger, service muet) : on ne redemande pas avant 7 jours,
// sinon chaque visite de / repaierait l'attente.
const IP_VU_KEY = 'lagenda:ville-ip-vu';
const IP_VU_MS = 7 * 24 * 3600 * 1000;
// Robots et outils de mesure (Googlebot, Lighthouse / PageSpeed…) : jamais de
// detection par IP. Ils voient l'accueil de Paris, comme un visiteur de
// l'etranger, sans attendre GeoJS ni etre rediriges.
// Meme liste d'outils que functions/analytics_guard.js (BOT_UA_RE), plus les
// apercus et rendus de Google et Bing.
const ROBOT_UA = /bot|crawl|spider|slurp|mediapartners|inspectiontool|googleother|bingpreview|lighthouse|pagespeed|gtmetrix|pingdom|headless|prerender|phantom|selenium|puppeteer|playwright|electron|facebookexternalhit|feedfetcher|dataprovider/i;
// Communes de France (API Decoupage administratif, Etalab) : interrogee
// seulement quand la ville tapee n'a pas de salle chez nous.
const COMMUNES_URL = 'https://geo.api.gouv.fr/communes';
// Hors de France, une IP ne choisit une ville que si elle en est proche
// (Geneve -> Annecy) ; sinon, Paris.
const ETRANGER_MAX_KM = 60;
const GRANDES_VILLES = 12;
const PARIS = 'paris';
// Message a afficher apres une navigation (ville detectee, commune sans salle).
const MSG_KEY = 'lagenda:ville-msg';
// Mesure d'audience du choix de ville (event ville_choisie) : en attente
// pendant une navigation, et ville posee par l'IP (pour savoir si on la corrige).
const EVT_KEY = 'lagenda:ville-evt';
const IP_KEY = 'lagenda:ville-ip';

// Rubriques et leurs adresses. Paris garde les siennes ; les autres villes ont
// celles que generate_city_pages.py fabrique (CITY_RUBRIQUES, meme liste —
// tests/test_city_pages.py y veille).
const PARIS_PATHS = {agenda:'/', salles:'/salles', artistes:'/artistes/', guides:'/guides', stats:'/stats'};
const CITY_RUBRIQUES = ['agenda', 'salles', 'artistes', 'guides', 'stats'];
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const META_VILLE = (() => {
  const v = (document.querySelector('meta[name="lagenda-ville"]') || {}).content || null;
  return v && SLUG_RE.test(v) ? v : null;
})();
// Fichier de Paris (export V1) : le seul que l'accueil de Paris exige.
const PARIS_PAYLOAD = '/data/events_upcoming.json';
// Guides qui existent dans toutes les villes (guide-view.js les sait filtrer) ;
// les autres (arrondissements, pages jour ou mois) sont propres a Paris.
const GUIDE_VILLE = /^\/(concerts\/(ce-soir|ce-week-end|cette-semaine|gratuits|pas-chers)|guide\/meilleurs-concerts-[a-z0-9-]+)\/?$/;
// Pages nationales, sans ville dans l'adresse : elles suivent la ville choisie
// (fiche artiste, page d'un producteur, annuaire des producteurs).
const NATIONALES = /^\/(artiste\/|producteur\/|producteurs(\/|\.html)?$)/;

const esc = s => (s==null?'':String(s)).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const $ = sel => document.querySelector(sel);
const fold = s => (s||'').toString().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g,'')
  .replace(/[^a-z0-9]+/g,' ').trim()
  .replace(/\bste\b/g,'sainte').replace(/\bst\b/g,'saint');

function km(lat1, lon1, lat2, lon2){
  const p = Math.PI / 180;
  const h = Math.sin((lat2-lat1)*p/2)**2 + Math.cos(lat1*p)*Math.cos(lat2*p)*Math.sin((lon2-lon1)*p/2)**2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

let INDEX = null;
// Sans prototype : ?ville=__proto__ ou constructor ne designent aucune ville.
let BY_SLUG = Object.create(null);
let CURRENT = null;
const listeners = [];

function nearest(lat, lon){
  let best = null;
  for (const v of INDEX.villes){
    const d = km(lat, lon, v.lat, v.lon);
    if (!best || d < best.km) best = {ville:v, km:d};
  }
  return best;
}

function readStore(){
  try { const v = localStorage.getItem(STORE_KEY); return v && SLUG_RE.test(v) ? v : null; }
  catch(_){ return null; }
}
function writeStore(slug){ try { localStorage.setItem(STORE_KEY, slug); } catch(_){} }
function ipRecemmentVue(){
  try { const t = +localStorage.getItem(IP_VU_KEY); return !!t && Date.now() - t < IP_VU_MS; }
  catch(_){ return false; }
}
function ipVue(){ try { localStorage.setItem(IP_VU_KEY, String(Date.now())); } catch(_){} }

function estRobot(){
  try { return ROBOT_UA.test(navigator.userAgent || ''); } catch(_){ return false; }
}

// ---------- mesure d'audience : ville_choisie ----------
// methode : ip (premiere visite), liste, commune (commune sans salle -> la
// ville la plus proche), autour (« Autour de moi »). depuis : la ville
// d'avant. corrige_ip : 'oui' quand la personne quitte la ville que l'IP lui
// avait donnee — la part des detections corrigees dit si l'IP tombe juste.
// Nom en litteral et inscrit dans ALLOWED_EVENTS (functions/analytics_guard.js),
// sinon /api/ping le jette.
function emit(params){
  if (typeof window.trackEvent === 'function') window.trackEvent('ville_choisie', params);
  else (window.__trackQueue = window.__trackQueue || []).push(['ville_choisie', params]);
}
// La page va etre quittee (redirection, changement de rubrique) : perf.js n'y
// est peut-etre pas encore charge, l'event part de la page d'arrivee.
function mesurer(params, navigue){
  if (!navigue) return emit(params);
  try { sessionStorage.setItem(EVT_KEY, JSON.stringify(params)); } catch(_){ emit(params); }
}
function mesureEnAttente(){
  let params = null;
  try { params = JSON.parse(sessionStorage.getItem(EVT_KEY) || 'null'); sessionStorage.removeItem(EVT_KEY); }
  catch(_){ return; }
  if (params) emit(params);
}
function villeParIp(slug){
  try { if (slug) localStorage.setItem(IP_KEY, slug); else localStorage.removeItem(IP_KEY); } catch(_){}
}
function lireVilleParIp(){ try { return localStorage.getItem(IP_KEY); } catch(_){ return null; } }

function fromUrl(){
  try { const v = new URLSearchParams(location.search).get('ville'); return v && SLUG_RE.test(v) ? v : null; }
  catch(_){ return null; }
}

// Ville que l'adresse designe : la <meta> des pages generees, sinon le 1er
// segment quand c'est une ville (guides servis par reecriture).
function pageVille(){
  if (META_VILLE) return META_VILLE;
  const seg = location.pathname.split('/')[1] || '';
  return seg !== PARIS && BY_SLUG[seg] ? seg : null;
}

// L'adresse sans le prefixe de ville : '/lyon/salles/' -> '/salles/'.
function pathInCity(){
  const v = pageVille(), path = location.pathname;
  return v && path.startsWith(`/${v}/`) ? path.slice(v.length + 1) : path;
}

// Rubrique de la page courante (agenda, salles…), null pour une page de detail.
function rubrique(){
  const seg = pathInCity().split('/').filter(Boolean)[0] || '';
  const map = {'': 'agenda', 'index.html': 'agenda', salles: 'salles', artistes: 'artistes', guides: 'guides', stats: 'stats'};
  return map[seg] || null;
}

function url(slug, rub){
  if (slug === PARIS) return PARIS_PATHS[rub] || '/';
  if (!CITY_RUBRIQUES.includes(rub)) rub = 'agenda';
  return rub === 'agenda' ? `/${slug}/` : `/${slug}/${rub}/`;
}

// Rubrique de taille d'une salle de province, sur les seuils des salles de
// Paris (venues-data.js : Intimiste jusqu'a 700 places, Grandes scenes de 1 000
// a 2 000, Zenith, Stades & Arena des 7 000). Sans capacite connue, aucune :
// on ne devine pas la taille d'une salle.
function taille(s){
  if (!s) return null;
  if (s.reseau === 'zenith') return 'Zénith';
  const cap = s.capacite;
  if (!cap) return null;
  if (cap >= 7000) return 'Stades & Arena';
  if (cap >= 1000 || s.reseau === 'arena') return 'Grandes scènes';
  return 'Intimiste';
}

// Les salles de Paris ont chacune leur couleur (venues-data.js) ; celles de
// province en tirent une de la meme gamme, toujours la meme pour une salle.
const COULEURS = ['#E8562C','#F59E32','#7BA9A6','#C9A44B','#B96F9A','#5E9DB6','#8A9A4F','#D97757','#6B9A7E','#E87A62'];
function couleur(slug){
  let h = 0;
  for (const c of String(slug || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COULEURS[h % COULEURS.length];
}

function say(html){ try { sessionStorage.setItem(MSG_KEY, html); } catch(_){} }
function said(){
  try { const m = sessionStorage.getItem(MSG_KEY); sessionStorage.removeItem(MSG_KEY); return m; }
  catch(_){ return null; }
}

async function detectByIp(){
  const ctrl = window.AbortController ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), IP_TIMEOUT_MS) : null;
  try {
    const r = await fetch(GEO_IP_URL, ctrl ? {signal: ctrl.signal} : undefined);
    if (!r.ok) return null;
    const g = await r.json();
    const lat = parseFloat(g.latitude), lon = parseFloat(g.longitude);
    if (!isFinite(lat) || !isFinite(lon)) return null;
    return {lat, lon, pays: g.country_code || '', commune: g.city || ''};
  } catch(_){
    return null;  // bloque (CSP, bloqueur de pub), lent ou hors ligne : Paris
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Ville de la page. Retourne null quand l'accueil part vers une autre ville
// (la page est en train d'etre quittee : rien a charger).
async function resolveInitial(){
  const own = pageVille();
  if (own && BY_SLUG[own]) return BY_SLUG[own];
  const rub = rubrique();
  if (rub && !(rub === 'agenda' && location.pathname === '/')) return BY_SLUG[PARIS];
  if (rub){
    // Robots et outils de mesure : l'accueil de Paris, toujours, quel que soit
    // le stockage (un outil qui le garde d'une page a l'autre serait renvoye
    // ailleurs que sur l'adresse qu'il mesure).
    if (estRobot()) return BY_SLUG[PARIS];
    // Accueil : la ville de la personne.
    // ?ville=paris (lien explicite) : choisi, donc memorise — sinon le logo et
    // le lien « Paris » ramenaient a la ville d'avant.
    const demandee = BY_SLUG[fromUrl()];
    if (demandee) writeStore(demandee.slug);
    let target = demandee || BY_SLUG[readStore()];
    if (!target && !ipRecemmentVue()){
      const ip = await (IP_EARLY || detectByIp());
      const n = ip && nearest(ip.lat, ip.lon);
      if (!(n && (ip.pays === 'FR' || n.km <= ETRANGER_MAX_KM))) ipVue();
      else {
        target = n.ville;
        writeStore(target.slug);
        villeParIp(target.slug);
        mesurer({ville: target.slug, methode: 'ip', depuis: '', corrige_ip: 'non'}, target.slug !== PARIS);
        if (target.slug !== PARIS) say(`Concerts ${esc(target.a_nom)}, d'après votre connexion. <button type="button">Changer</button>`);
      }
    }
    if (target && target.slug !== PARIS){
      location.replace(url(target.slug, 'agenda'));
      return null;
    }
    return BY_SLUG[PARIS];
  }
  // Fiche artiste et pages producteur (nationales) : la ville choisie. Le
  // reste parle de Paris.
  if (NATIONALES.test(location.pathname)) return BY_SLUG[readStore()] || BY_SLUG[PARIS];
  return BY_SLUG[PARIS];
}

// ---------- rendu de la ville courante dans la page ----------
function applyCurrent(){
  const t = $('#ville-trigger');
  if (!t) return;
  $('#ville-nom').textContent = CURRENT.nom;
  t.title = `Concerts ${CURRENT.a_nom} — changer de ville`;
  t.hidden = INDEX.villes.length < 2;
}

function select(slug, note, methode){
  const v = BY_SLUG[slug];
  if (!v) return;
  writeStore(slug);
  closePicker();
  if (CURRENT && CURRENT.slug === slug){ if (note) toast(note); return; }
  const depuis = CURRENT ? CURRENT.slug : '';
  const parIp = lireVilleParIp();
  villeParIp(null);
  const evt = {ville: slug, methode: methode || 'liste', depuis, corrige_ip: parIp && parIp === depuis ? 'oui' : 'non'};
  const guide = pathInCity();
  if (/^\/(concerts|guide)\//.test(guide)){
    // Un guide : le meme dans l'autre ville, sinon les guides de cette ville.
    if (note) say(note);
    mesurer(evt, true);
    location.assign(slug === PARIS ? guide : GUIDE_VILLE.test(guide) ? `/${slug}${guide}` : url(slug, 'guides'));
    return;
  }
  const rub = rubrique();
  if (rub || !listeners.length){
    // La meme rubrique dans l'autre ville (ou son agenda, depuis une page qui
    // ne sait pas se rearranger).
    if (note) say(note);
    mesurer(evt, true);
    location.assign(url(slug, rub || 'agenda'));
    return;
  }
  CURRENT = v;
  applyCurrent();
  mesurer(evt, false);
  if (note) toast(note);
  for (const fn of listeners){ try { fn(v); } catch(err){ console.error('[villes]', err); } }
}

// Message bref sous l'en-tete (ville detectee, commune sans salle).
let toastTimer = null;
function toast(html){
  let el = $('#ville-toast');
  if (!el){
    el = document.createElement('div');
    el.id = 'ville-toast';
    el.className = 'ville-toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.innerHTML = html;
  el.classList.add('on');
  const btn = el.querySelector('button');
  if (btn) btn.onclick = () => { el.classList.remove('on'); openPicker(); };
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('on'), 7000);
}

// ---------- selecteur ----------
const DLG_HTML = `
<div class="ville-scrim" id="ville-scrim"></div>
<div class="ville-dlg" id="ville-dlg" role="dialog" aria-modal="true" aria-labelledby="ville-dlg-title" aria-hidden="true">
  <div class="ville-head">
    <h2 id="ville-dlg-title">Choisir une ville</h2>
    <button type="button" class="ville-x" id="ville-x" aria-label="Fermer">✕</button>
  </div>
  <div class="ville-search">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.2" y2="16.2"></line></svg>
    <input id="ville-input" type="search" placeholder="Ville ou commune…" autocomplete="off" spellcheck="false" aria-controls="ville-list" aria-label="Chercher une ville ou une commune">
  </div>
  <button type="button" class="ville-near" id="ville-near">Autour de moi</button>
  <p class="ville-note" id="ville-note" aria-live="polite"></p>
  <div class="ville-list" id="ville-list" role="listbox" aria-label="Villes"></div>
</div>`;

let dlgReady = false;
let rows = [];
let sel = 0;
let communesTimer = null;
let communesSeq = 0;

function ensureDialog(){
  if (dlgReady) return;
  dlgReady = true;
  document.body.insertAdjacentHTML('beforeend', DLG_HTML);
  $('#ville-scrim').addEventListener('click', closePicker);
  $('#ville-x').addEventListener('click', closePicker);
  const input = $('#ville-input');
  input.addEventListener('input', () => renderList(input.value));
  input.addEventListener('keydown', ev => {
    if (ev.key === 'Escape'){ ev.preventDefault(); closePicker(); }
    else if (ev.key === 'ArrowDown'){ ev.preventDefault(); move(1); }
    else if (ev.key === 'ArrowUp'){ ev.preventDefault(); move(-1); }
    else if (ev.key === 'Enter'){ ev.preventDefault(); if (input.value.trim() && rows[sel]) rows[sel].click(); }
  });
  const near = $('#ville-near');
  if (!navigator.geolocation) near.hidden = true;
  else near.addEventListener('click', locateMe);
  document.addEventListener('keydown', ev => {
    if (ev.key === 'Escape' && isOpen()) closePicker();
  });
}

function isOpen(){ return dlgReady && $('#ville-dlg').classList.contains('open'); }

function openPicker(){
  ensureDialog();
  const input = $('#ville-input');
  input.value = '';
  setNote('');
  renderList('');
  $('#ville-scrim').classList.add('open');
  const dlg = $('#ville-dlg');
  dlg.classList.add('open');
  dlg.setAttribute('aria-hidden', 'false');
  $('#ville-trigger')?.setAttribute('aria-expanded', 'true');
  document.body.style.overflow = 'hidden';
  setTimeout(() => input.focus(), 30);
}

function closePicker(){
  if (!isOpen()) return;
  $('#ville-scrim').classList.remove('open');
  const dlg = $('#ville-dlg');
  dlg.classList.remove('open');
  dlg.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
  const t = $('#ville-trigger');
  if (t){ t.setAttribute('aria-expanded', 'false'); t.focus(); }
}

function setNote(html){ const n = $('#ville-note'); if (n) n.innerHTML = html; }

function plural(n, one, many){ return `${n.toLocaleString('fr-FR')} ${n > 1 ? many : one}`; }

function rowHtml(v, sub){
  const on = CURRENT && CURRENT.slug === v.slug;
  return `<button type="button" class="ville-row${on ? ' on' : ''}" role="option" aria-selected="${on}" data-slug="${esc(v.slug)}">
    <span class="nm">${esc(v.nom)}</span>
    <span class="sub">${sub || ''}</span>
    <span class="n">${plural(v.dates, 'date', 'dates')}</span>
  </button>`;
}

// Liste par defaut : les grandes villes, puis toutes les villes, a plat et par
// ordre alphabetique (pas de regroupement par region : on cherche une ville,
// pas une region).
function defaultHtml(){
  const top = INDEX.villes.slice().sort((a,b) => b.dates - a.dates).slice(0, GRANDES_VILLES);
  const all = INDEX.villes.slice().sort((a,b) => a.nom.localeCompare(b.nom, 'fr'));
  const html = [`<div class="ville-group"><h3>Les plus actives</h3><div class="ville-chips">`];
  for (const v of top){
    const on = CURRENT && CURRENT.slug === v.slug;
    html.push(`<button type="button" class="ville-chip${on ? ' on' : ''}" role="option" aria-selected="${on}" data-slug="${esc(v.slug)}">${esc(v.nom)}</button>`);
  }
  html.push(`</div></div><div class="ville-group"><h3>Toutes les villes · ${all.length}</h3>`);
  for (const v of all) html.push(rowHtml(v, v.communes.length ? `avec ${esc(v.communes.slice(0,3).join(', '))}${v.communes.length > 3 ? '…' : ''}` : ''));
  html.push('</div>');
  return html.join('');
}

// Recherche locale : nom de la ville ou d'une commune rattachee.
function matchLocal(q){
  const needle = fold(q);
  const hits = [];
  for (const v of INDEX.villes){
    const names = [v.nom].concat(v.communes);
    let best = null;
    for (const n of names){
      const f = fold(n);
      const rank = f === needle ? 0 : f.startsWith(needle) ? 1 : (' ' + f).includes(' ' + needle) ? 2 : f.includes(needle) ? 3 : -1;
      if (rank >= 0 && (!best || rank < best.rank)) best = {rank, via: n === v.nom ? null : n};
    }
    if (best) hits.push({ville: v, ...best});
  }
  return hits.sort((a,b) => a.rank - b.rank || b.ville.dates - a.ville.dates).slice(0, 20);
}

function renderList(q){
  const list = $('#ville-list');
  clearTimeout(communesTimer);
  const query = (q || '').trim();
  if (!query){
    list.innerHTML = defaultHtml();
  } else {
    const hits = matchLocal(query);
    list.innerHTML = hits.length
      ? hits.map(h => rowHtml(h.ville, h.via ? `${esc(h.via)} → ${esc(h.ville.nom)}` : esc(h.ville.region))).join('')
      : `<p class="ville-empty">Aucune salle suivie à « ${esc(query)} ». Recherche de la commune…</p>`;
    // Commune sans salle chez nous (Vannes, Bayonne…) : on la situe, puis on
    // propose les villes les plus proches.
    if (!hits.length && fold(query).length >= 2){
      communesTimer = setTimeout(() => searchCommunes(query), 250);
    }
  }
  wireRows();
}

async function searchCommunes(query){
  const seq = ++communesSeq;
  const list = $('#ville-list');
  let found = [];
  try {
    const qs = new URLSearchParams({nom: query, fields: 'nom,centre,codeDepartement', boost: 'population', limit: '5'});
    const r = await fetch(`${COMMUNES_URL}?${qs}`);
    if (r.ok) found = await r.json();
  } catch(_){}
  if (seq !== communesSeq || !isOpen()) return;
  found = (found || []).filter(c => c.centre && c.centre.coordinates);
  if (!found.length){
    list.innerHTML = `<p class="ville-empty">Aucune commune « ${esc(query)} ».</p>`;
    wireRows();
    return;
  }
  const html = [];
  for (const c of found){
    const [lon, lat] = c.centre.coordinates;
    const n = nearest(lat, lon);
    html.push(`<button type="button" class="ville-row" role="option" data-slug="${esc(n.ville.slug)}" data-from="${esc(c.nom)}" data-km="${Math.round(n.km)}">
      <span class="nm">${esc(c.nom)} <small>(${esc(c.codeDepartement)})</small></span>
      <span class="sub">concerts à ${esc(n.ville.nom)}, ${Math.round(n.km)} km</span>
      <span class="n">${plural(n.ville.dates, 'date', 'dates')}</span>
    </button>`);
  }
  list.innerHTML = `<p class="ville-empty">Pas de salle suivie à « ${esc(query)} » : la ville la plus proche.</p>` + html.join('');
  wireRows();
}

function wireRows(){
  rows = Array.from(document.querySelectorAll('#ville-list [data-slug]'));
  sel = 0;
  rows.forEach((el, i) => {
    el.addEventListener('click', () => {
      const from = el.dataset.from;
      const v = BY_SLUG[el.dataset.slug];
      select(el.dataset.slug, from && v ? `Pas de salle suivie à ${esc(from)} : voici les concerts ${esc(v.a_nom)}, à ${esc(el.dataset.km)} km.` : null,
             from ? 'commune' : 'liste');
    });
    el.addEventListener('mouseenter', () => { sel = i; highlight(); });
  });
  highlight();
}

function highlight(){
  const typing = !!($('#ville-input') && $('#ville-input').value.trim());
  rows.forEach((el, i) => el.classList.toggle('sel', typing && i === sel));
  if (typing && rows[sel]) rows[sel].scrollIntoView({block: 'nearest'});
}

function move(delta){
  if (!rows.length) return;
  if (!$('#ville-input').value.trim()) return;
  sel = (sel + delta + rows.length) % rows.length;
  highlight();
}

// « Autour de moi » : position precise du navigateur, demandee au clic
// seulement (jamais a l'arrivee : l'IP suffit pour une premiere ville).
function locateMe(){
  const btn = $('#ville-near');
  btn.disabled = true;
  setNote('Localisation…');
  navigator.geolocation.getCurrentPosition(pos => {
    btn.disabled = false;
    const n = nearest(pos.coords.latitude, pos.coords.longitude);
    if (!n){ setNote(''); return; }
    select(n.ville.slug, `Ville la plus proche de vous : ${esc(n.ville.nom)}, à ${Math.round(n.km)} km.`, 'autour');
  }, () => {
    btn.disabled = false;
    setNote('Position indisponible. Choisissez une ville dans la liste.');
  }, {timeout: 8000, maximumAge: 600000});
}

// ---------- dates de la page ----------
// Un fichier n'est demande qu'une fois par page, quel que soit le module qui le
// lit (accueil, recherche, salles, guides).
const FICHIERS = Object.create(null);
function fichier(src){
  if (!FICHIERS[src]) FICHIERS[src] = fetch(src).then(r => {
    if (!r.ok) throw new Error(`${src} ${r.status}`);
    return r.json();
  }).then(d => { if (!Array.isArray(d)) throw new Error(`${src} : pas une liste`); return d; });
  return FICHIERS[src];
}

// Fichiers de la page quand l'index des villes manque (reseau, cache) : ceux de
// la ville de l'adresse — une page de ville ne montre jamais Paris sous son nom
// (generate_city_pages.py ecrit /data/villes/<slug>.json pour chaque ville) —
// sinon Paris.
function sourcesSansIndex(){
  if (META_VILLE && META_VILLE !== PARIS) return [`/data/villes/${META_VILLE}.json`];
  const m = location.pathname.match(/^\/([a-z0-9]+(?:-[a-z0-9]+)*)\/(?:concerts|guide)\//);
  if (m && m[1] !== PARIS) return [`/data/villes/${m[1]}.json`];
  return [PARIS_PAYLOAD];
}

// Fichiers que la page lira probablement, demandes avant la reponse de l'index
// (et de l'IP a la premiere visite) : l'accueil de Paris n'attend plus
// villes.json puis GeoJS pour commencer a telecharger ses concerts. Pas de
// devinette quand l'accueil va sans doute partir vers une autre ville.
function probables(){
  if (META_VILLE || location.pathname !== '/') return sourcesSansIndex();
  const memo = estRobot() ? null : fromUrl() || readStore();
  return memo && memo !== PARIS ? [] : [PARIS_PAYLOAD];
}

// Index des villes qui ne repond pas (reseau tres lent) : au-dela, les
// fichiers de l'adresse, plutot qu'une page vide.
const INDEX_TIMEOUT_MS = 8000;
async function sources(){
  const v = await Promise.race([ready, new Promise(r => setTimeout(() => r(null), INDEX_TIMEOUT_MS))]);
  return v && Array.isArray(v.sources) && v.sources.length ? v.sources : sourcesSansIndex();
}

// Le 1er fichier est exige (payload de Paris, ou celui de la ville) : s'il
// manque, la promesse est rejetee et la page affiche son erreur. Les suivants
// (salles de province du rayon de Paris) completent : absents (index en cache
// plus vieux que le deploiement), la page s'affiche sans eux. Une date presente
// dans deux fichiers n'est rendue qu'une fois.
async function fetchSources(list){
  const lists = await Promise.all((list || []).map((src, i) =>
    i === 0 ? fichier(src) : fichier(src).catch(err => { console.warn('[villes]', err); return []; })));
  const seen = new Set();
  return [].concat(...lists).filter(e => e && (!e.id || (!seen.has(e.id) && seen.add(e.id))));
}

async function events(){
  probables().forEach(src => fichier(src).catch(() => {}));
  return fetchSources(await sources());
}

// Lien vers l'agenda d'une ville (pied de page « Concerts en France », page
// /villes/) : c'est un choix de ville, memorise comme dans le selecteur. Sans
// cela, le logo (/) ramenait a la ville d'avant, et Paris n'etait joignable que
// par le selecteur.
document.addEventListener('click', ev => {
  const a = ev.target && ev.target.closest && ev.target.closest('.foot-villes a[href], a[data-ville]');
  if (!a) return;
  const m = (a.getAttribute('data-ville') ? `/${a.getAttribute('data-ville')}/` : a.getAttribute('href') || '').match(/^\/(?:([a-z0-9]+(?:-[a-z0-9]+)*)\/)?$/);
  if (!m) return;
  const slug = m[1] || PARIS;
  if (slug !== PARIS && !BY_SLUG[slug]) return;
  const depuis = CURRENT ? CURRENT.slug : '';
  writeStore(slug);
  if (slug !== depuis) mesurer({ville: slug, methode: 'lien', depuis, corrige_ip: 'non'}, true);
}, true);

// ---------- demarrage ----------
// Premiere visite sur l'accueil : l'IP est demandee tout de suite, en meme
// temps que l'index des villes, pour ne pas retarder l'affichage.
const IP_EARLY = location.pathname === '/' && !fromUrl() && !readStore() && !estRobot() && !ipRecemmentVue() ? detectByIp() : null;
const ready = fetch('/data/villes.json')
  .then(r => r.ok ? r.json() : null)
  .then(async index => {
    if (!index || !Array.isArray(index.villes) || !index.villes.length) return null;
    if (!index.salles || typeof index.salles !== 'object') index.salles = {};
    INDEX = index;
    BY_SLUG = Object.create(null);
    for (const v of index.villes) if (v && typeof v.slug === 'string') BY_SLUG[v.slug] = v;
    const ville = await resolveInitial();
    if (!ville) return new Promise(() => {});  // l'accueil part vers une autre ville
    CURRENT = ville;
    const wire = () => {
      const t = $('#ville-trigger');
      if (t) t.addEventListener('click', openPicker);
      // Page de ville servie par reecriture (guides) : ses liens de rubrique
      // pointent encore sur Paris ; les pages generees ont deja les bons. Le
      // pied de page « Concerts en France » mene a Paris lui-meme : intact.
      if (!META_VILLE && CURRENT.slug !== PARIS && pageVille() === CURRENT.slug){
        for (const [rub, path] of Object.entries(PARIS_PATHS)){
          const target = url(CURRENT.slug, rub);
          for (const href of new Set([path, path.endsWith('/') ? path.slice(0, -1) : path + '/'])){
            if (href) document.querySelectorAll(`a[href="${href}"]`).forEach(a => {
              if (!a.closest('.foot-villes')) a.setAttribute('href', target);
            });
          }
        }
      }
      applyCurrent();
      mesureEnAttente();
      const msg = said();
      if (msg) toast(msg);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
    else wire();
    return CURRENT;
  })
  .catch(err => { console.warn('[villes] indisponible, Paris seul', err); return null; });

window.LAgendaVilles = {
  ready,
  current: () => CURRENT,
  choisie: () => BY_SLUG[readStore()] || null,
  villes: () => (INDEX ? INDEX.villes : []),
  metaVille: META_VILLE,
  sources,
  fetchSources,
  events,
  salle: name => (INDEX && Object.prototype.hasOwnProperty.call(INDEX.salles, name) && INDEX.salles[name]) || null,
  sallesDeLaVille: () => {
    if (!INDEX || !CURRENT) return [];
    return Object.entries(INDEX.salles)
      .filter(([, s]) => s.ville === CURRENT.slug)
      .map(([venue, s]) => ({venue, ...s}));
  },
  salles: () => (INDEX ? Object.entries(INDEX.salles).map(([venue, s]) => ({venue, ...s})) : []),
  salleParSlug: slug => {
    if (!INDEX || !slug) return null;
    const hit = Object.entries(INDEX.salles).find(([, s]) => s.slug === slug);
    return hit ? {venue: hit[0], ...hit[1]} : null;
  },
  ville: slug => BY_SLUG[slug] || null,
  taille,
  couleur,
  url,
  onChange: fn => { if (typeof fn === 'function') listeners.push(fn); },
  open: openPicker,
};
})();
